import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY = { showroom_name: '', contact_person: '', phone: '', city: '', address: '', company_id: '' };

export default function ShowroomSetup() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [showrooms, setShowrooms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get('/api/showrooms'); setShowrooms(r.data); }
    catch { setShowrooms([]); }
    if (isAdmin) {
      try { const r = await api.get('/api/companies'); setCompanies(r.data); }
      catch { setCompanies([]); }
    }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const filtered = showrooms.filter(s =>
    s.showroom_name.toLowerCase().includes(search.toLowerCase()) ||
    (s.city || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.contact_person || '').toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => { setForm(EMPTY); setEditing(null); setShowForm(true); };
  const openEdit = (s) => {
    setForm({
      showroom_name: s.showroom_name, contact_person: s.contact_person || '',
      phone: s.phone || '', city: s.city || '', address: s.address || '',
      company_id: s.company_id ? String(s.company_id) : ''
    });
    setEditing(s.id); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/api/showrooms/${editing}`, form);
      else         await api.post('/api/showrooms', form);
      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (s) => {
    if (!window.confirm(`${s.is_active ? 'Deactivate' : 'Reactivate'} showroom "${s.showroom_name}"?`)) return;
    try {
      await api.put(`/api/showrooms/${s.id}`, { ...s, is_active: !s.is_active });
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!confirmDelete('showroom', name)) return;
    try { await api.delete(`/api/showrooms/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  const activeCount   = showrooms.filter(s => s.is_active).length;
  const inactiveCount = showrooms.filter(s => !s.is_active).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Showroom Setup</h1>
          <p className="text-gray-500 text-sm">
            {activeCount} active · {inactiveCount} inactive
          </p>
        </div>
        <button onClick={openAdd}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ➕ Add Showroom
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, city, contact person..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Showroom' : 'Add Showroom'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Showroom Name *</label>
                <input type="text" value={form.showroom_name} required
                  onChange={e => setForm({ ...form, showroom_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input type="text" value={form.contact_person}
                  onChange={e => setForm({ ...form, contact_person: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="text" value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" value={form.city}
                  onChange={e => setForm({ ...form, city: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea rows={2} value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {/* Company */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                {isAdmin ? (
                  <select value={form.company_id} required
                    onChange={e => setForm({ ...form, company_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Select Company —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <input type="text" disabled value={user?.company_name || 'Your Company'}
                    className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500" />
                )}
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" disabled={saving}
                  className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-60">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Add Showroom'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl animate-pulse mb-2">🏪</div>
            <p>Loading showrooms...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">🏪</div>
            <p>No showrooms found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Showroom Name', 'Contact Person', 'Phone', 'City', isAdmin ? 'Company' : '', 'Status', 'Actions']
                    .filter(Boolean).map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(s => (
                  <tr key={s.id} className={`hover:bg-gray-50 transition-colors ${!s.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-800">{s.showroom_name}</td>
                    <td className="px-4 py-3 text-gray-600">{s.contact_person || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{s.phone || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{s.city || '—'}</td>
                    {isAdmin && <td className="px-4 py-3 text-gray-600 text-xs">{s.company_name || '—'}</td>}
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(s)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium">✏️ Edit</button>
                        <button onClick={() => handleToggleActive(s)}
                          className={`text-xs font-medium ${s.is_active ? 'text-orange-500 hover:text-orange-700' : 'text-green-600 hover:text-green-800'}`}>
                          {s.is_active ? '⏸ Deactivate' : '▶ Activate'}
                        </button>
                        {isAdmin && (
                          <button onClick={() => handleDelete(s.id, s.showroom_name)}
                            className="text-red-500 hover:text-red-700 text-xs font-medium">🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
